// src/lib/wakeLock.js
// Keep the device screen awake during active scoring. Thin singleton wrapper
// around the Screen Wake Lock API (navigator.wakeLock).
//
// Why a module-level singleton (mirrors the _geoWatchId watcher in gps.js):
// React StrictMode double-mounts and the scorecard's per-hole effect churn
// could otherwise fire overlapping request()/release() pairs and leak a
// dangling sentinel. Holding the sentinel + the "desired" intent at module
// scope means at most one real OS wake lock exists no matter how many callers
// toggle it.
//
// Why a visibilitychange re-acquire (mirrors restartGeoWatch in gps.js):
// the browser AUTO-RELEASES the wake lock whenever the tab/PWA is backgrounded.
// After the user checks their gallery and returns, the screen would silently
// stop staying awake unless we re-request on the way back to visible. That
// background/foreground cycle is the exact Android failure class the whole
// native-app effort exists to fix, so the resume path is load-bearing.
//
// Graceful fallback: older iOS Safari (and any engine without the API) has no
// navigator.wakeLock. Every path here is guarded and swallows errors — a
// missing API or a denied request must NEVER throw or block scoring.

let _sentinel = null   // the live WakeLockSentinel, or null when not held
let _desired = false   // caller wants the lock held while the page is visible
let _visBound = false  // is our visibilitychange listener installed?
let _acquiring = false // in-flight request() guard (prevents double-acquire)

// True when the Screen Wake Lock API is usable in this engine.
export function isSupported() {
  return (
    typeof navigator !== 'undefined' &&
    !!navigator.wakeLock &&
    typeof navigator.wakeLock.request === 'function'
  )
}

// True when we currently hold an OS wake lock. Exposed mostly for tests.
export function isHeld() {
  return !!_sentinel
}

// Acquire the underlying sentinel if we should hold it and don't already.
// Never throws — a denied/unsupported/hidden state simply leaves us unheld.
async function _acquire() {
  if (!_desired || !isSupported()) return
  if (_sentinel || _acquiring) return
  // Requesting while the document is hidden throws in some engines; skip it —
  // the visibilitychange handler re-acquires once we're visible again.
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return
  _acquiring = true
  try {
    const s = await navigator.wakeLock.request('screen')
    // A caller may have released between the await above and here; if intent
    // was dropped, immediately release the freshly-acquired lock.
    if (!_desired) {
      try { await s.release() } catch { /* best-effort */ }
      return
    }
    _sentinel = s
    // The browser fires 'release' on background auto-release; drop our handle
    // so the visibilitychange re-acquire path knows it must request again.
    try {
      s.addEventListener?.('release', () => { if (_sentinel === s) _sentinel = null })
    } catch { /* addEventListener absent on stub — fine */ }
  } catch {
    /* denied / not visible / unsupported — never throw, never block scoring */
  } finally {
    _acquiring = false
  }
}

// Re-acquire on return to visibility (the browser auto-released on background).
function _onVisibility() {
  if (typeof document === 'undefined') return
  if (document.visibilityState !== 'visible') return
  if (_desired && !_sentinel) _acquire()
}

// Request that the screen stay awake. Idempotent — calling twice holds one
// lock. Installs the visibilitychange re-acquire listener on first request.
export async function requestWakeLock() {
  _desired = true
  if (!_visBound && typeof document !== 'undefined' && document.addEventListener) {
    document.addEventListener('visibilitychange', _onVisibility)
    _visBound = true
  }
  await _acquire()
}

// Read the per-device "Keep screen awake" preference. Default OFF
// (owner-specified): only an explicit stored '1' means ON — unset, '0', or
// any other value reads as OFF. Storage is injectable for the node-env unit
// suite (no localStorage global there); the app calls it with no argument.
export function readWakeLockPref(storage) {
  try {
    const s = storage ?? (typeof localStorage !== 'undefined' ? localStorage : null)
    return s?.getItem('tf_wakelock_pref') === '1'
  } catch {
    return false // private mode / storage denied — fall back to the OFF default
  }
}

// Pure gate for the scoring page's wake-lock effect: hold the lock ONLY when
// the user toggle is ON AND a round is actively being scored. Keeps the lock
// off the Sign-In / "No active event" / "No active round" static screens.
export function shouldHoldWakeLock(toggleOn, hasActiveRound) {
  return toggleOn === true && hasActiveRound === true
}

// Release the wake lock and stop wanting it. Idempotent — safe to call when
// nothing is held. Removes the visibilitychange listener so nothing lingers
// after the scoring page unmounts.
export async function releaseWakeLock() {
  _desired = false
  if (_visBound && typeof document !== 'undefined' && document.removeEventListener) {
    document.removeEventListener('visibilitychange', _onVisibility)
    _visBound = false
  }
  const s = _sentinel
  _sentinel = null
  if (s) {
    try { await s.release() } catch { /* best-effort */ }
  }
}
